import React from "react";
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Stack,
  TextField,
  InputLabel,
  Select,
  FormHelperText,
  RadioGroup,
  Radio,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import {
  defaultAdvancePerGroup,
  fixtureNeedsMoreTeams,
  knockoutQualifiers,
} from "components/pages/Gestion/tournament-wizard/gestion-league-wizard";

const SETUP_MANUAL = "manual";
const SETUP_AUTO = "auto_fixture";

const LeagueTournamentWizardFields = ({
  formData,
  setFormData,
  leagueTournaments,
  tournamentHasStarted,
  isEdit,
  hideYear = false,
  hideCopy = false,
  hideSetupMode = false,
  teamCount,
}) => {
  const intl = useIntl();
  const setupMode = formData.league_setup_mode || SETUP_MANUAL;
  const groupsEnabled = formData.groups_enabled !== false;
  const knockoutEnabled = Boolean(formData.knockout_enabled);
  const generateFixtures = Boolean(formData.generate_fixtures);
  const fixtureBlocked = fixtureNeedsMoreTeams(teamCount);
  const qualifiers = knockoutQualifiers(formData.groups_count, formData.advance_per_group);

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSetupModeChange = (mode) => {
    setFormData((prev) => ({
      ...prev,
      league_setup_mode: mode,
      generate_fixtures: mode === SETUP_AUTO,
      // "Ida y vuelta" is opt-in (D5); switching setup mode keeps whatever the user chose.
      is_home_and_away: Boolean(prev.group_home_and_away ?? prev.is_home_and_away ?? false),
      group_home_and_away: Boolean(prev.group_home_and_away ?? prev.is_home_and_away ?? false),
    }));
  };

  return (
    <Stack gap={3}>
      {!isEdit && !hideSetupMode ? (
        <FormControl>
          <Typography variant="subtitle2" gutterBottom>
            {intl.formatMessage({ id: "league.wizard.setupMode" })}
          </Typography>
          <RadioGroup
            value={setupMode}
            onChange={(event) => handleSetupModeChange(event.target.value)}
          >
            <FormControlLabel
              value={SETUP_MANUAL}
              control={<Radio />}
              label={intl.formatMessage({ id: "league.wizard.setupModeManual" })}
            />
            <FormHelperText sx={{ mt: -1, ml: 4, mb: 1 }}>
              {intl.formatMessage({ id: "league.wizard.setupModeManualHint" })}
            </FormHelperText>
            <FormControlLabel
              value={SETUP_AUTO}
              control={<Radio />}
              label={intl.formatMessage({ id: "league.wizard.setupModeAuto" })}
            />
            <FormHelperText sx={{ mt: -1, ml: 4 }}>
              {intl.formatMessage({ id: "league.wizard.setupModeAutoHint" })}
            </FormHelperText>
          </RadioGroup>
        </FormControl>
      ) : null}

      {!hideYear ? (
        <TextField
          required
          fullWidth
          type="number"
          label={intl.formatMessage({ id: "league.wizard.year" })}
          value={formData.year ?? ""}
          disabled={tournamentHasStarted}
          inputProps={{ min: 1900, max: 2100 }}
          onChange={(event) => setField("year", event.target.value)}
        />
      ) : null}

      {!isEdit && !hideCopy && leagueTournaments.length > 0 ? (
        <FormControl fullWidth>
          <InputLabel id="copy-lineups-label">{intl.formatMessage({ id: "league.wizard.copyLineups" })}</InputLabel>
          <Select
            labelId="copy-lineups-label"
            label={intl.formatMessage({ id: "league.wizard.copyLineups" })}
            value={formData.copy_lineup_from_tournament_id || ""}
            onChange={(event) => setField("copy_lineup_from_tournament_id", event.target.value || null)}
          >
            <MenuItem value="">{intl.formatMessage({ id: "league.wizard.copyLineupsNone" })}</MenuItem>
            {leagueTournaments.map((item) => (
              <MenuItem key={item.revaId || item.id} value={item.revaId || item.id}>
                {item.name} {item.year ? `(${item.year})` : ""}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{intl.formatMessage({ id: "league.wizard.copyLineupsHint" })}</FormHelperText>
        </FormControl>
      ) : null}

      {!isEdit ? (
        <>
          <Divider />
          <Typography variant="subtitle1">{intl.formatMessage({ id: "league.wizard.scaffoldGroups" })}</Typography>

          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={groupsEnabled}
                  onChange={(event) => setField("groups_enabled", event.target.checked)}
                />
              }
              label={intl.formatMessage({ id: "league.wizard.groupsEnabled" })}
            />
          </FormControl>

          {groupsEnabled ? (
            <Stack gap={2}>
              <TextField
                fullWidth
                type="number"
                label={intl.formatMessage({ id: "league.wizard.groupsCount" })}
                value={formData.groups_count ?? 1}
                inputProps={{ min: 1, max: 26 }}
                onChange={(event) => setField("groups_count", Number(event.target.value) || 1)}
              />
              <TextField
                fullWidth
                type="number"
                label={intl.formatMessage({ id: "league.wizard.teamsPerGroup" })}
                value={formData.teams_per_group ?? 4}
                inputProps={{ min: 2, max: 64 }}
                helperText={intl.formatMessage({ id: "league.wizard.teamsPerGroupHint" })}
                onChange={(event) => setField("teams_per_group", Number(event.target.value) || 2)}
              />
              <TextField
                fullWidth
                type="number"
                label={intl.formatMessage({ id: "league.wizard.advancePerGroup" })}
                value={formData.advance_per_group ?? ""}
                inputProps={{ min: 0, max: 32 }}
                helperText={intl.formatMessage({ id: "league.wizard.advancePerGroupHint" })}
                onChange={(event) =>
                  setField("advance_per_group", event.target.value === "" ? 0 : Number(event.target.value))
                }
              />
              {knockoutEnabled && qualifiers.total >= 2 ? (
                <FormHelperText data-testid="knockout-qualifiers-summary">
                  {intl.formatMessage(
                    { id: "league.wizard.knockoutQualifiers" },
                    {
                      total: qualifiers.total,
                      stage: intl.formatMessage({ id: `league.wizard.knockoutStage.${qualifiers.stage}` }),
                    },
                  )}
                </FormHelperText>
              ) : null}
              {knockoutEnabled && qualifiers.total < 2 ? (
                <FormHelperText data-testid="knockout-needs-two">
                  {intl.formatMessage({ id: "league.wizard.knockoutNeedsTwo" })}
                </FormHelperText>
              ) : null}
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formData.group_home_and_away ?? formData.is_home_and_away)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          group_home_and_away: checked,
                          is_home_and_away: checked,
                        }));
                      }}
                    />
                  }
                  label={intl.formatMessage({ id: "league.wizard.groupHomeAndAway" })}
                />
              </FormControl>
            </Stack>
          ) : null}

          <Divider />
          <Typography variant="subtitle1">{intl.formatMessage({ id: "league.wizard.scaffoldKnockout" })}</Typography>
          <RevaText variant="caption" color="text.secondary">
            {intl.formatMessage({ id: "league.wizard.scaffoldKnockoutHint" })}
          </RevaText>

          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={knockoutEnabled}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setField("knockout_enabled", checked);
                    if (checked) {
                      setField("show_bracket", true);
                      if (!(formData.advance_per_group > 0)) {
                        setField("advance_per_group", defaultAdvancePerGroup(formData.groups_count));
                      }
                    }
                  }}
                />
              }
              label={intl.formatMessage({ id: "league.wizard.knockoutEnabled" })}
            />
          </FormControl>

          {knockoutEnabled ? (
            <Stack gap={1}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formData.knockout_home_and_away)}
                      onChange={(event) => setField("knockout_home_and_away", event.target.checked)}
                    />
                  }
                  label={intl.formatMessage({ id: "league.wizard.knockoutHomeAndAway" })}
                />
              </FormControl>
              {formData.knockout_home_and_away ? (
                <FormControl sx={{ pl: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formData.knockout_final_home_and_away)}
                        onChange={(event) => setField("knockout_final_home_and_away", event.target.checked)}
                      />
                    }
                    label={intl.formatMessage({ id: "league.wizard.knockoutFinalHomeAndAway" })}
                  />
                </FormControl>
              ) : null}
            </Stack>
          ) : null}

          <Divider />
          <Typography variant="subtitle1">{intl.formatMessage({ id: "league.wizard.scaffoldFixtures" })}</Typography>

          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={generateFixtures && !fixtureBlocked}
                  disabled={setupMode === SETUP_AUTO || fixtureBlocked}
                  onChange={(event) => setField("generate_fixtures", event.target.checked)}
                />
              }
              label={intl.formatMessage({ id: "league.wizard.generateFixtures" })}
            />
            <RevaText variant="caption" color="text.secondary">
              {intl.formatMessage({ id: "league.wizard.generateFixturesHint" })}
            </RevaText>
          </FormControl>

          {fixtureBlocked ? (
            <Alert severity="warning" data-testid="fixture-skipped-warning">
              {intl.formatMessage({ id: "league.wizard.fixtureSkippedFewTeams" })}
            </Alert>
          ) : null}

          {generateFixtures && !fixtureBlocked ? (
            <TextField
              fullWidth
              type="number"
              label={intl.formatMessage({ id: "league.wizard.matchdaysCount" })}
              value={formData.matchdays_count ?? ""}
              inputProps={{ min: 1, max: 52 }}
              helperText={intl.formatMessage({ id: "league.wizard.matchdaysCountHint" })}
              onChange={(event) =>
                setField("matchdays_count", event.target.value === "" ? null : Number(event.target.value))
              }
            />
          ) : null}
        </>
      ) : (
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(formData.is_home_and_away)}
                disabled={tournamentHasStarted}
                onChange={(event) => setField("is_home_and_away", event.target.checked)}
              />
            }
            label={intl.formatMessage({ id: "league.wizard.homeAndAway" })}
          />
        </FormControl>
      )}
    </Stack>
  );
};

export default LeagueTournamentWizardFields;
